import { z } from 'zod';
export const currencySchema = z.string().regex(/^[A-Z]{3}$/, 'Waluta musi być kodem ISO 4217');
export const uuidSchema = z.string().uuid();
export const availabilitySchema = z.enum(['in_stock', 'out_of_stock', 'unknown']);
export const checkStatusSchema = z.enum(['ok', 'pending', 'error', 'never']);
export const monitoringStatusSchema = z.enum(['active', 'pending', 'checking', 'error', 'unsupported', 'paused']);
export const categorySchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(80),
    slug: z.string().min(1).max(80),
    icon: z.string().max(40),
    productCount: z.number().int().nonnegative(),
});
export const storeSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(120),
    domain: z.string().min(1).max(255),
});
export const pricePointSchema = z.object({
    id: uuidSchema,
    priceMinor: z.number().int().nonnegative(),
    currency: currencySchema,
    recordedAt: z.string().datetime(),
    source: z.enum(['manual', 'worker', 'demo']),
    availability: availabilitySchema.optional(),
});
export const productOfferSchema = z.object({
    id: uuidSchema,
    store: storeSchema,
    url: z.string().url(),
    currentPriceMinor: z.number().int().nonnegative(),
    previousPriceMinor: z.number().int().nonnegative().nullable(),
    targetPriceMinor: z.number().int().nonnegative().nullable(),
    shippingPriceMinor: z.number().int().nonnegative().nullable(),
    currency: currencySchema,
    availability: availabilitySchema,
    checkStatus: checkStatusSchema,
    monitoringStatus: monitoringStatusSchema,
    lastCheckedAt: z.string().datetime().nullable(),
    lastSuccessfulAt: z.string().datetime().nullable(),
    lastError: z.string().nullable(),
    history: z.array(pricePointSchema),
});
export const productSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(180),
    imageUrl: z.string().url().nullable(),
    note: z.string().max(1000).nullable(),
    category: categorySchema,
    offers: z.array(productOfferSchema).min(1),
    createdAt: z.string().datetime(),
    isDemo: z.boolean(),
});
export const createProductSchema = z.object({
    url: z.string().url('Podaj poprawny adres URL').max(2048),
    name: z.string().trim().min(2, 'Nazwa musi mieć co najmniej 2 znaki').max(180),
    imageUrl: z.union([z.literal(''), z.string().url('Podaj poprawny URL zdjęcia')]).optional(),
    storeName: z.string().trim().min(2).max(120),
    initialPriceMinor: z.number().int().positive('Cena musi być większa od zera'),
    targetPriceMinor: z.number().int().positive().nullable(),
    currency: currencySchema.default('PLN'),
    categoryId: uuidSchema,
    note: z.string().trim().max(1000).optional(),
});
export const dashboardSummarySchema = z.object({
    trackedProducts: z.number().int().nonnegative(),
    recentDrops: z.number().int().nonnegative(),
    reachedTargets: z.number().int().nonnegative(),
    potentialSavingsMinor: z.number().int(),
    currency: currencySchema,
});
export const dashboardSchema = z.object({
    summary: dashboardSummarySchema,
    products: z.array(productSchema),
    attentionProductIds: z.array(uuidSchema),
    marketHistory: z.array(z.object({ date: z.string(), valueMinor: z.number().int().nonnegative() })),
    generatedAt: z.string().datetime(),
    isDemo: z.boolean(),
});
export const readSourceSchema = z.enum(['official_api', 'json_ld', 'html_meta', 'store_adapter', 'browser']);
export const priceEventTypeSchema = z.enum([
    'price_drop', 'price_increase', 'target_reached', 'new_historical_minimum',
    'back_in_stock', 'out_of_stock',
]);
export const normalizedMeasurementSchema = z.object({
    offerId: uuidSchema,
    productName: z.string().min(1).max(300),
    url: z.string().url(),
    store: z.string().min(1).max(120),
    priceMinor: z.number().int().positive(),
    currency: currencySchema,
    availability: availabilitySchema,
    shippingPriceMinor: z.number().int().nonnegative().nullable(),
    checkedAt: z.string().datetime(),
    source: readSourceSchema,
    status: z.literal('success'),
});
export const failedMeasurementSchema = z.object({
    offerId: uuidSchema,
    productName: z.string().min(1).max(300),
    url: z.string().url(),
    store: z.string().min(1).max(120),
    checkedAt: z.string().datetime(),
    source: readSourceSchema.nullable(),
    status: z.enum(['error', 'unsupported', 'blocked']),
    errorCode: z.string().min(1).max(80),
    errorMessage: z.string().min(1).max(1000),
    retryable: z.boolean(),
});
export const priceEventSchema = z.object({
    id: uuidSchema,
    offerId: uuidSchema,
    type: priceEventTypeSchema,
    previousPriceMinor: z.number().int().nonnegative().nullable(),
    currentPriceMinor: z.number().int().nonnegative().nullable(),
    currency: currencySchema,
    occurredAt: z.string().datetime(),
});
export const monitoringRunSchema = z.object({
    id: uuidSchema,
    status: z.enum(['running', 'succeeded', 'partial', 'failed']),
    triggerSource: z.enum(['manual', 'github_actions', 'scheduler']),
    offersRequested: z.number().int().nonnegative(),
    offersSucceeded: z.number().int().nonnegative(),
    offersFailed: z.number().int().nonnegative(),
    eventsCreated: z.number().int().nonnegative(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
});
export const monitoringHealthSchema = z.object({
    configured: z.boolean(),
    activeOffers: z.number().int().nonnegative(),
    failingOffers: z.number().int().nonnegative(),
    unsupportedOffers: z.number().int().nonnegative(),
    message: z.string().optional(),
    lastRun: monitoringRunSchema.pick({ id: true, status: true, startedAt: true, finishedAt: true, offersSucceeded: true, offersFailed: true }).nullable(),
});
export const manualCheckResultSchema = z.object({
    accepted: z.boolean(),
    runId: uuidSchema.nullable(),
    status: z.enum(['queued', 'succeeded', 'partial', 'failed']),
    offersSucceeded: z.number().int().nonnegative(),
    offersFailed: z.number().int().nonnegative(),
    message: z.string(),
});
